import { ApiPaths, api } from "@/lib/api";
import type {
  Address,
  AddressSchemaCatalog,
  Campaign,
  FieldAppliesTo,
  FieldDefinition,
  MembershipParty,
  MembershipPartyRole,
  Paginated,
} from "@/lib/types";

export const CAMPAIGNS_PAGE_SIZE = 30;

export async function listCampaigns(
  opts: { includeArchived?: boolean; page?: number; pageSize?: number } = {},
): Promise<Paginated<Campaign>> {
  const params: Record<string, string> = {
    page: String(opts.page ?? 1),
    page_size: String(opts.pageSize ?? CAMPAIGNS_PAGE_SIZE),
  };
  if (opts.includeArchived) params.include_archived = "true";
  const res = await api.get<Paginated<Campaign>>(ApiPaths.campaigns, {
    params,
  });
  return res.data;
}


export interface CreateCampaignPayload {
  name: string;
  description?: string;
  /** ISO 3166-1 alpha-2 the campaign targets — scopes the Excel
   * template's country-specific custom fields. Blank = no scope. */
  country?: string;
  /** ISO date (YYYY-MM-DD). Optional planning window — campaigns
   * without a window still work; progress falls back to closed /
   * total sessions when dates are missing. */
  start_date?: string | null;
  end_date?: string | null;
  /** AI Campaign & Smart Queue opt-in. `ai_assisted` runs the outbound
   * SMS/email/voice-AI orchestration; `standard` (default) is the manual
   * booking-wave flow. Immutable once launched. */
  campaign_kind?: "standard" | "ai_assisted";
  /** Optional IANA tz override; blank inherits the org timezone. */
  timezone?: string;
}


export async function createCampaign(payload: CreateCampaignPayload): Promise<Campaign> {
  const res = await api.post<Campaign>(ApiPaths.campaigns, payload);
  return res.data;
}


export interface PatchCampaignPayload {
  name?: string;
  description?: string;
  country?: string;
  start_date?: string | null;
  end_date?: string | null;
  archived?: boolean;
  /** Setting ended=true stamps Campaign.ended_at (manual close);
   * ended=false reopens. */
  ended?: boolean;
  /** standard | public_sector — public_sector uses the French/STOP
   * booking-wave SMS default (#10 Phase 3). */
  campaign_type?: string;
  /** standard | ai_assisted — AI Campaign & Smart Queue opt-in. Rejected
   * with 400 once the campaign is launched. */
  campaign_kind?: "standard" | "ai_assisted";
  /** Optional IANA tz override; blank inherits the org timezone. */
  timezone?: string;
  /** Slot-rhythm config (Campaigns/Sessions v2 #5). */
  slot_minutes?: number;
  break_minutes?: number;
  slack_minutes?: number;
  break_mode?: "none" | "after_n_calls" | "after_minutes";
  calls_before_break?: number;
  minutes_before_break?: number;
  long_break_minutes?: number;
}


export async function patchCampaign(
  id: string,
  payload: PatchCampaignPayload,
): Promise<Campaign> {
  const res = await api.patch<Campaign>(ApiPaths.campaign(id), payload);
  return res.data;
}


export async function deleteCampaign(id: string): Promise<void> {
  await api.delete(ApiPaths.campaign(id));
}


// ---------- P1+P4 — Addresses ----------

export async function listContactAddresses(
  contactId: string,
): Promise<Address[]> {
  const res = await api.get<Address[]>(ApiPaths.contactAddresses(contactId));
  return res.data;
}

export type AddressWritePayload = Partial<
  Omit<Address, "id" | "contact" | "formatted" | "created_at" | "updated_at">
>;

export async function createContactAddress(
  contactId: string,
  payload: AddressWritePayload,
): Promise<Address> {
  const res = await api.post<Address>(
    ApiPaths.contactAddresses(contactId),
    payload,
  );
  return res.data;
}

export async function patchContactAddress(
  contactId: string,
  addressId: string,
  payload: AddressWritePayload,
): Promise<Address> {
  const res = await api.patch<Address>(
    ApiPaths.contactAddress(contactId, addressId),
    payload,
  );
  return res.data;
}

export async function deleteContactAddress(
  contactId: string,
  addressId: string,
): Promise<void> {
  await api.delete(ApiPaths.contactAddress(contactId, addressId));
}


// ---------- P2 — Membership parties ----------

export async function listMembershipParties(
  campaignId: string,
  membershipId: string,
): Promise<MembershipParty[]> {
  const res = await api.get<MembershipParty[]>(
    ApiPaths.campaignMembershipParties(campaignId, membershipId),
  );
  return res.data;
}

export async function addMembershipParty(
  campaignId: string,
  membershipId: string,
  payload: {
    contact_id: string;
    role: MembershipPartyRole;
    notes?: string;
  },
): Promise<MembershipParty> {
  const res = await api.post<MembershipParty>(
    ApiPaths.campaignMembershipParties(campaignId, membershipId),
    payload,
  );
  return res.data;
}

export async function removeMembershipParty(
  campaignId: string,
  membershipId: string,
  partyId: string,
): Promise<void> {
  await api.delete(
    ApiPaths.campaignMembershipParty(campaignId, membershipId, partyId),
  );
}


// ---------- P4 — FieldDefinition catalog ----------

export interface ListFieldDefinitionsOpts {
  applies_to?: FieldAppliesTo;
  country_scope?: string;
  project_category_scope?: string;
  role_scope?: string;
  include_archived?: boolean;
  include_system?: boolean;
  /** When set, lists that campaign's own (campaign-scoped) fields.
   *  When omitted, lists only org-wide fields (campaign IS NULL). */
  campaign?: string;
}

export async function listFieldDefinitions(
  opts: ListFieldDefinitionsOpts = {},
): Promise<FieldDefinition[]> {
  const params: Record<string, string> = {};
  if (opts.applies_to) params.applies_to = opts.applies_to;
  if (opts.country_scope) params.country_scope = opts.country_scope;
  if (opts.project_category_scope)
    params.project_category_scope = opts.project_category_scope;
  if (opts.role_scope) params.role_scope = opts.role_scope;
  if (opts.include_archived) params.include_archived = "true";
  if (opts.include_system === false) params.include_system = "false";
  if (opts.campaign) params.campaign = opts.campaign;
  const res = await api.get<FieldDefinition[]>(ApiPaths.adminFields, {
    params,
  });
  return res.data;
}

export type FieldDefinitionWritePayload = Partial<
  Pick<
    FieldDefinition,
    | "key"
    | "label"
    | "help_text"
    | "kind"
    | "applies_to"
    | "country_scope"
    | "project_category_scope"
    | "role_scope"
    | "is_required"
    | "is_pii"
    | "options_jsonb"
    | "regex_pattern"
    | "min_value"
    | "max_value"
  >
> & {
  /** Set to scope the new field to a single campaign (it overrides the
   *  org field of the same key for that campaign). Omit for org-wide. */
  campaign?: string;
};

export async function createFieldDefinition(
  payload: FieldDefinitionWritePayload,
): Promise<FieldDefinition> {
  const res = await api.post<FieldDefinition>(ApiPaths.adminFields, payload);
  return res.data;
}

export async function patchFieldDefinition(
  id: string,
  payload: FieldDefinitionWritePayload,
): Promise<FieldDefinition> {
  const res = await api.patch<FieldDefinition>(
    ApiPaths.adminField(id),
    payload,
  );
  return res.data;
}

export async function archiveFieldDefinition(id: string): Promise<void> {
  await api.delete(ApiPaths.adminField(id));
}

/** Org opt-out of a system-provided field (#04). `disabled=true` removes
 * it from the org's schemas + Excel templates; `false` restores it. */
export async function systemToggleField(
  key: string,
  applies_to: string,
  disabled: boolean,
): Promise<FieldDefinition> {
  const res = await api.post<FieldDefinition>(
    ApiPaths.adminFieldsSystemToggle,
    { key, applies_to, disabled },
  );
  return res.data;
}

export interface ResolveSchemaOpts {
  applies_to: FieldAppliesTo;
  country?: string;
  project_category?: string;
  role?: string;
  /** Layer this campaign's own fields over the org catalog (a campaign
   *  field overrides the org one by key) — the effective set for that
   *  campaign's beneficiary forms / import template. */
  campaign?: string;
}

/** Returns the schema applicable to the given target. SPA uses this to
 *  render dynamic forms — Contact create with country=ES gets DNI/CIF,
 *  country=FR gets SIRET, etc. */
/** Per-country address schema catalog — used to render the
 *  country-aware Address form (required + optional fields,
 *  country_specific extras, postal regex). */
export async function loadAddressSchemas(): Promise<AddressSchemaCatalog> {
  const res = await api.get<AddressSchemaCatalog>(
    ApiPaths.adminAddressSchemas,
  );
  return res.data;
}

export async function resolveFieldSchema(
  opts: ResolveSchemaOpts,
): Promise<FieldDefinition[]> {
  const params: Record<string, string> = { applies_to: opts.applies_to };
  if (opts.country) params.country = opts.country;
  if (opts.project_category) params.project_category = opts.project_category;
  if (opts.role) params.role = opts.role;
  if (opts.campaign) params.campaign = opts.campaign;
  const res = await api.get<FieldDefinition[]>(ApiPaths.adminFieldsSchema, {
    params,
  });
  return res.data;
}
